// src/components/actes/NouvelActeModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  query, 
  where,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadMultipleFiles } from '../../services/uploadService';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Calendar,
  Clock,
  User,
  FileText,
  Upload,
  Camera,
  Euro,
  Repeat,  // AJOUT de l'icône Repeat
  AlertCircle,
  Check,
  Search,
  Filter,
  Edit
} from 'lucide-react';

const NouvelActeModal = ({ isOpen, onClose, preselectedAnimalId = null }) => {
  const [step, setStep] = useState(1); // 1: Sélection animaux, 2: Détails acte
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  
  // États pour la sélection d'animaux
  const [animaux, setAnimaux] = useState([]);
  const [selectedAnimaux, setSelectedAnimaux] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEspece, setFilterEspece] = useState('tous');
  const [tagColors, setTagColors] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'nom', direction: 'asc' });
  
  // États pour l'acte
  const [typesActes, setTypesActes] = useState({});
  const [intervenants, setIntervenants] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedActe, setSelectedActe] = useState(null);
  
const [acteData, setActeData] = useState({
  dateActe: new Date().toISOString().split('T')[0],
  heureActe: '', // Heure optionnelle
  statut: 'planifie',
  intervenantId: '',
  description: '',
  notes: '',
  cout: '',
  documents: [],
  
  // Rappel pour CET acte uniquement
rappel: {
  active: false,
  joursAvant: 7
},
recurrence: {
  active: false,
  typeRecurrence: 'mensuel',
  prochaineEcheance: '',
  rappelRecurrent: {
    active: false,
    joursAvant: 7
  },
  editingDate: false
},


  champsSpecifiques: {}
});

  
  // États pour l'upload
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Fonction pour afficher un toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2000);
  };

  // Composant Toast
  const Toast = () => {
    if (!toast) return null;
    
    return (
      <div className="fixed top-4 right-4 z-[60] animate-pulse">
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        } text-white`}>
          {toast.type === 'success' ? (
            <Check className="h-5 w-5" />
          ) : (
            <X className="h-5 w-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      </div>
    );
  };

  // Fonction pour obtenir la couleur d'un tag
  const getTagColor = (tagId) => {
    if (!tagId) return '#cccccc';
    const found = tagColors.find(tc => tc.id === parseInt(tagId));
    return found ? found.codeHex : '#cccccc';
  };

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      if (preselectedAnimalId) {
        setSelectedAnimaux([preselectedAnimalId]);
        setStep(2);
      }
    }
  }, [isOpen, preselectedAnimalId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Charger les couleurs des tags
      const tagColorsSnap = await getDocs(collection(db, "tagCouleursAnimaux"));
      let colorsData = [];
      tagColorsSnap.forEach(doc => {
        const data = doc.data();
        if (data.id && data.codeHex) {
          colorsData.push({ 
            id: data.id,
            nom: data.nom || '',
            codeHex: data.codeHex,
            ordre: data.ordre || data.id
          });
        }
      });
      colorsData.sort((a, b) => a.ordre - b.ordre);
      setTagColors(colorsData);
      
      // Charger les animaux
      const animauxSnap = await getDocs(
        query(collection(db, 'animaux'), orderBy('nom'))
      );
      const animauxData = animauxSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAnimaux(animauxData);
      
      // Charger les types d'actes actifs
      const typesActesSnap = await getDocs(
        query(collection(db, 'typesActes'), where('actif', '==', true))
      );
      const typesActesData = typesActesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Grouper par catégorie
      const grouped = typesActesData.reduce((acc, acte) => {
        if (!acc[acte.typeCategorie]) {
          acc[acte.typeCategorie] = [];
        }
        acc[acte.typeCategorie].push(acte);
        return acc;
      }, {});
      
      // Trier les actes dans chaque catégorie
      Object.keys(grouped).forEach(cat => {
        grouped[cat].sort((a, b) => a.ordre - b.ordre || a.nomActe.localeCompare(b.nomActe));
      });
      
      setTypesActes(grouped);
      
      // Charger les intervenants actifs
      const intervenantsSnap = await getDocs(
        query(collection(db, 'intervenants'), where('actif', '==', true))
      );
      const intervenantsData = intervenantsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setIntervenants(intervenantsData.sort((a, b) => a.nom.localeCompare(b.nom)));
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnimalToggle = (animalId) => {
    setSelectedAnimaux(prev => {
      if (prev.includes(animalId)) {
        return prev.filter(id => id !== animalId);
      } else {
        return [...prev, animalId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredAnimaux = getFilteredAnimaux();
    if (selectedAnimaux.length === filteredAnimaux.length) {
      setSelectedAnimaux([]);
    } else {
      setSelectedAnimaux(filteredAnimaux.map(a => a.id));
    }
  };

  const getFilteredAnimaux = () => {
    const filtered = animaux.filter(animal => {
      const matchesSearch = animal.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           animal.race?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           animal.sire?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesEspece = filterEspece === 'tous' || 
                           (filterEspece === 'equide' && animal.type === 'equide') ||
                           (filterEspece === 'animal' && animal.type === 'animal');
      return matchesSearch && matchesEspece;
    });

    // Appliquer le tri
    return filtered.sort((a, b) => {
      const { key, direction } = sortConfig;
      let aValue = a[key];
      let bValue = b[key];

      // Gérer les valeurs nulles/undefined
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';

      // Cas spéciaux pour certaines colonnes
      if (key === 'dateNaissance') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (key === 'tag') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else if (key === 'type') {
        // Trier par type puis categorie
        aValue = `${a.type || ''}_${a.categorie || ''}`;
        bValue = `${b.type || ''}_${b.categorie || ''}`;
      }

      // Comparaison
      let comparison = 0;
      if (aValue > bValue) comparison = 1;
      if (aValue < bValue) comparison = -1;

      return direction === 'asc' ? comparison : -comparison;
    });
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortButton = ({ columnKey, children }) => (
    <button
      onClick={() => handleSort(columnKey)}
      className="flex items-center gap-1 hover:text-gray-700 transition-colors"
    >
      {children}
      <div className="flex flex-col ml-1">
        <ChevronUp 
          className={`h-3 w-3 -mb-1 ${
            sortConfig.key === columnKey && sortConfig.direction === 'asc' 
              ? 'text-black' 
              : 'text-gray-300'
          }`} 
        />
        <ChevronDown 
          className={`h-3 w-3 ${
            sortConfig.key === columnKey && sortConfig.direction === 'desc' 
              ? 'text-black' 
              : 'text-gray-300'
          }`} 
        />
      </div>
    </button>
  );

  const handleActeSelection = (categorie, acte) => {
    setSelectedCategory(categorie);
    setSelectedActe(acte);
    
    // Initialiser les champs spécifiques avec les valeurs par défaut
    if (acte.champsSpecifiques && acte.champsSpecifiques.length > 0) {
      const defaultValues = {};
      acte.champsSpecifiques.forEach(champ => {
        if (champ.defaut !== undefined) {
          defaultValues[champ.nom] = champ.defaut;
        } else if (champ.type === 'multiselect') {
          defaultValues[champ.nom] = [];
        } else {
          defaultValues[champ.nom] = '';
        }
      });
      setActeData(prev => ({
        ...prev,
        champsSpecifiques: defaultValues
      }));
    }
  };

  const handleFileUpload = async (files) => {
    setUploadingFiles(true);
    
    try {
      const result = await uploadMultipleFiles(
        files, 
        'actes',
        (progress) => {
          console.log(`Upload: ${progress.current}/${progress.total} - ${progress.percentage}%`);
        }
      );
      
      if (result.success.length > 0) {
        setActeData(prev => ({
          ...prev,
          documents: [...prev.documents, ...result.success]
        }));
      }
      
      if (result.errors.length > 0) {
        result.errors.forEach(err => {
          showToast(`Erreur upload ${err.fileName}`, 'error');
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      showToast('Erreur lors de l\'upload', 'error');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const validateForm = () => {
    if (selectedAnimaux.length === 0) {
      showToast('Veuillez sélectionner au moins un animal', 'error');
      return false;
    }
    
    if (!selectedActe) {
      showToast('Veuillez sélectionner un type d\'acte', 'error');
      return false;
    }
    
    if (!acteData.dateActe) {
  showToast('Veuillez renseigner la date', 'error');
  return false;
}
    
    // Vérifier les champs obligatoires spécifiques
    if (selectedActe.champsSpecifiques) {
      for (const champ of selectedActe.champsSpecifiques) {
        if (champ.obligatoire && !acteData.champsSpecifiques[champ.nom]) {
          showToast(`Le champ "${champ.label}" est obligatoire`, 'error');
          return false;
        }
      }
    }
    
    return true;
  };

  // Fonction pour calculer la prochaine date de récurrence
  const calculateNextDate = (currentDate, recurrenceType) => {
    const date = new Date(currentDate);
    
    switch (recurrenceType) {
      case 'hebdomadaire':
        date.setDate(date.getDate() + 7);
        break;
      case 'mensuel':
      case 'mensuelle':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'trimestriel':
      case 'trimestrielle':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'semestriel':
      case 'semestrielle':
        date.setMonth(date.getMonth() + 6);
        break;
      case 'annuel':
      case 'annuelle':
        date.setFullYear(date.getFullYear() + 1);
        break;
      default:
        date.setMonth(date.getMonth() + 1);
        break;
    }
    
    // Formater la date en YYYY-MM-DD
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Fonction pour créer le prochain acte récurrent
  const createNextRecurringActe = async (parentActeId, parentActeData, animalId, animalNom, intervenantNom) => {
    try {
      const nextDate = calculateNextDate(parentActeData.dateActe, parentActeData.recurrence.typeRecurrence);
      
      const newActeData = {
        nomActe: parentActeData.nomActe,
        typeCategorie: parentActeData.typeCategorie,
        typeActeId: parentActeData.typeActeId || null,
        animalId: animalId,
        animalNom: animalNom,
        dateActe: nextDate,
        heureActe: parentActeData.heureActe || '09:00',
        statut: 'planifie',
        dateRealisation: null,
        intervenantId: parentActeData.intervenantId || null,
        intervenantNom: intervenantNom || null,
        cout: parentActeData.cout || null,
        description: parentActeData.description || '',
        notes: parentActeData.notes || '',
        couleur: parentActeData.couleur || null,
        champsSpecifiques: parentActeData.champsSpecifiques || {},
        // Utiliser le rappel configuré pour les récurrences
        rappel: parentActeData.recurrence?.rappelRecurrent || null,
        recurrence: parentActeData.recurrence,
        documents: [], // Pas de documents pour le nouvel acte
        historique: [
          {
            date: new Date().toISOString(),
            action: 'création',
            details: `Acte créé automatiquement par récurrence de l'acte du ${new Date(parentActeData.dateActe).toLocaleDateString('fr-FR')}`
          }
        ],
        acteParentId: parentActeId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'actes'), newActeData);
      
      return {
        id: docRef.id,
        date: nextDate
      };
    } catch (error) {
      console.error('Erreur lors de la création de l\'acte récurrent:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    
    try {
      // Créer un acte pour chaque animal sélectionné
      const promises = selectedAnimaux.map(async (animalId) => {
        const animal = animaux.find(a => a.id === animalId);
        const intervenant = intervenants.find(i => i.id === acteData.intervenantId);
        
        const acteDoc = {
  animalId: animalId,
  animalNom: animal?.nom || '',
  typeActeId: selectedActe.id,
  typeCategorie: selectedCategory,
  nomActe: selectedActe.nomActe,
  couleur: selectedActe.couleur || '#6b7280',
  
  dateActe: acteData.dateActe,
  heureActe: acteData.heureActe,
  statut: acteData.statut,
  dateRealisation: acteData.statut === 'realise' ? new Date().toISOString() : null,
  
  intervenantId: acteData.intervenantId || null,
  intervenantNom: intervenant ? `${intervenant.prenom} ${intervenant.nom}` : null,
  
  description: acteData.description,
  notes: acteData.notes,
  cout: acteData.cout ? parseFloat(acteData.cout) : null,
  
  champsSpecifiques: acteData.champsSpecifiques,
  documents: acteData.documents,
  


// Rappel pour CET acte (seulement si planifié)
rappel: acteData.statut === 'planifie' ? {
  active: acteData.rappel.active,
  joursAvant: acteData.rappel.joursAvant
} : null,

// Récurrence avec son propre système de rappel
recurrence: acteData.recurrence.active ? {
  active: true,
  typeRecurrence: acteData.recurrence.typeRecurrence,
  rappelRecurrent: acteData.recurrence.rappelRecurrent
} : null,


  
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  historique: [{
    date: new Date().toISOString(),
    action: 'création',
    details: 'Acte créé'
  }]
};

        
        // Créer l'acte principal
        const docRef = await addDoc(collection(db, 'actes'), acteDoc);
        
        // IMPORTANT : Si l'acte est créé en "réalisé" ET a une récurrence active
        // Il faut créer immédiatement le prochain acte
        if (acteData.statut === 'realise' && acteData.recurrence.active) {
          try {
            const nextActe = await createNextRecurringActe(
              docRef.id,
              acteDoc,
              animalId,
              animal?.nom || '',
              intervenant ? `${intervenant.prenom} ${intervenant.nom}` : null
            );
            
            // Mettre à jour l'acte parent avec les infos du prochain acte
            await updateDoc(doc(db, 'actes', docRef.id), {
              prochaineEcheanceCreee: true,
              prochaineEcheanceId: nextActe.id,
              prochaineEcheanceDate: nextActe.date,
              updatedAt: new Date().toISOString()
            });
            
            console.log(`Prochain acte récurrent créé : ${nextActe.date}`);
          } catch (error) {
            console.error('Erreur lors de la création de l\'acte récurrent:', error);
            // On ne bloque pas la création de l'acte principal si la récurrence échoue
          }
        }
        
        return docRef;
      });
      
      await Promise.all(promises);
      
      // Message de succès
      showToast(`${selectedAnimaux.length} acte(s) créé(s) avec succès`);
      
      // Réinitialiser et fermer après un court délai
      setTimeout(() => {
        onClose();
        resetForm();
      }, 500);
      
    } catch (error) {
      console.error('Erreur lors de la création des actes:', error);
      showToast('Erreur lors de la création des actes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedAnimaux([]);
    setSelectedCategory('');
    setSelectedActe(null);
    setActeData({
      dateActe: new Date().toISOString().split('T')[0],
      heureActe: new Date().toTimeString().slice(0, 5),
      statut: 'planifie',
      intervenantId: '',
      description: '',
      notes: '',
      cout: '',
      documents: [],
      recurrence: {
        active: false,
        typeRecurrence: 'mensuel',
        prochaineEcheance: '',
        rappelJoursAvant: 7
      },
      champsSpecifiques: {}
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <Toast />
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* En-tête */}
          <div className="p-6 border-b flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {step === 1 ? 'Sélectionner les animaux' : 'Créer un acte'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto p-6">
            {step === 1 ? (
              // Étape 1: Sélection des animaux
              <div>
                <div className="mb-4 flex gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un animal..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                    />
                  </div>
                  
                  <select
                    value={filterEspece}
                    onChange={(e) => setFilterEspece(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                  >
                    <option value="tous">Tous les animaux</option>
                    <option value="equide">Équidés</option>
                    <option value="animal">Autres animaux</option>
                  </select>
                  
                  <button
                    onClick={handleSelectAll}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {selectedAnimaux.length === getFilteredAnimaux().length ? 
                      'Désélectionner tout' : 'Sélectionner tout'}
                  </button>
                </div>
                
                <div className="mb-4 text-sm text-gray-600 flex items-center justify-between">
                  <span>{selectedAnimaux.length} animal(aux) sélectionné(s)</span>
                  {selectedAnimaux.length > 0 && (
                    <span className="text-xs">
                      Cliquez sur un animal pour le désélectionner
                    </span>
                  )}
                </div>
                
                {/* En-tête de la liste */}
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="col-span-1 text-center">Sél.</div>
                  <div className="col-span-3">
                    <SortButton columnKey="nom">Nom</SortButton>
                  </div>
                  <div className="col-span-2">
                    <SortButton columnKey="sire">SIRE</SortButton>
                  </div>
                  <div className="col-span-2">
                    <SortButton columnKey="dateNaissance">Naissance</SortButton>
                  </div>
                  <div className="col-span-1">Sexe</div>
                  <div className="col-span-2">
                    <SortButton columnKey="type">Type</SortButton>
                  </div>
                  <div className="col-span-1 text-center">
                    <SortButton columnKey="tag">Tag</SortButton>
                  </div>
                </div>
                
                {/* Liste des animaux */}
                <div className="bg-white border border-gray-200 rounded-b-lg max-h-96 overflow-y-auto">
                  {getFilteredAnimaux().length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Aucun animal trouvé
                    </div>
                  ) : (
                    getFilteredAnimaux().map(animal => (
                      <div
                        key={animal.id}
                        onClick={() => handleAnimalToggle(animal.id)}
                        className={`
                          grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-100 cursor-pointer transition-all items-center
                          ${selectedAnimaux.includes(animal.id) 
                            ? 'bg-gray-50' 
                            : 'hover:bg-gray-50'}
                        `}
                      >
                        {/* Checkbox */}
                        <div className="col-span-1 flex justify-center">
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center
                            ${selectedAnimaux.includes(animal.id) 
                              ? 'border-black bg-black' 
                              : 'border-gray-300'}
                          `}>
                            {selectedAnimaux.includes(animal.id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                        </div>
                        
                        {/* Nom avec photo */}
                        <div className="col-span-3 flex items-center gap-2">
                          {animal.photoUrl && (
                            <img
                              src={animal.photoUrl}
                              alt={animal.nom}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          )}
                          <span className="font-medium">{animal.nom}</span>
                        </div>
                        
                        {/* SIRE */}
                        <div className="col-span-2 text-sm">
                          {animal.sire || <span className="text-gray-400">-</span>}
                        </div>
                        
                        {/* Date de naissance */}
                        <div className="col-span-2 text-sm">
                          {animal.dateNaissance ? 
                            new Date(animal.dateNaissance).toLocaleDateString('fr-FR') : 
                            <span className="text-gray-400">-</span>
                          }
                        </div>
                        
                        {/* Sexe */}
                        <div className="col-span-1 text-sm">
                          {animal.sexe || <span className="text-gray-400">-</span>}
                        </div>
                        
                        {/* Type (équidé avec catégorie ou animal) */}
                        <div className="col-span-2 text-sm">
                          {animal.type === 'equide' ? (
                            <span className={`
                              inline-flex px-2 py-1 text-xs font-medium rounded-full
                              ${animal.categorie === 'Cheval' ? 'bg-blue-100 text-blue-800' :
                                animal.categorie === 'Poney' ? 'bg-purple-100 text-purple-800' :
                                animal.categorie === 'Âne' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'}
                            `}>
                              {animal.categorie || 'Équidé'}
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                              Animal
                            </span>
                          )}
                        </div>
                        
                        {/* Tag (pastille de couleur sans numéro) */}
                        <div className="col-span-1 flex justify-center">
                          {animal.tag ? (
                            <div 
                              className="w-6 h-6 rounded-full border border-gray-300"
                              style={{ backgroundColor: getTagColor(animal.tag) }}
                              title={`Tag ${animal.tag}`}
                            />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              // Étape 2: Détails de l'acte
              <div className="space-y-6">
                {/* Animaux sélectionnés */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-900 mb-2">
                    Animaux concernés ({selectedAnimaux.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedAnimaux.map(animalId => {
                      const animal = animaux.find(a => a.id === animalId);
                      return (
                        <span
                          key={animalId}
                          className="px-3 py-1 bg-white rounded-full text-sm border border-gray-300"
                        >
                          {animal?.nom}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Sélection du type d'acte */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type d'acte *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                    {Object.keys(typesActes).map(categorie => (
                      <button
                        key={categorie}
                        onClick={() => setSelectedCategory(categorie)}
                        className={`
                          px-3 py-2 border rounded-lg text-sm font-medium transition-colors
                          ${selectedCategory === categorie
                            ? 'border-black bg-gray-50 text-black'
                            : 'border-gray-300 hover:border-gray-400'}
                        `}
                      >
                        {categorie}
                      </button>
                    ))}
                  </div>
                  
                  {selectedCategory && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {typesActes[selectedCategory].map(acte => (
                        <button
                          key={acte.id}
                          onClick={() => handleActeSelection(selectedCategory, acte)}
                          className={`
                            px-3 py-2 border rounded-lg text-sm transition-all flex items-center gap-2
                            ${selectedActe?.id === acte.id
                              ? 'border-black bg-black text-white'
                              : 'border-gray-300 hover:border-gray-400'}
                          `}
                        >
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: acte.couleur || '#6b7280' }}
                          />
                          {acte.nomActe}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Date et heure */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={acteData.dateActe}
                      onChange={(e) => setActeData({ ...acteData, dateActe: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heure
                    </label>
                    <input
                      type="time"
                      value={acteData.heureActe}
                      onChange={(e) => setActeData({ ...acteData, heureActe: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                    />
                  </div>
                </div>

                {/* Intervenant et statut */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Intervenant
                    </label>
                    <select
                      value={acteData.intervenantId}
                      onChange={(e) => setActeData({ ...acteData, intervenantId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                    >
                      <option value="">Aucun intervenant</option>
                      {intervenants.map(intervenant => (
                        <option key={intervenant.id} value={intervenant.id}>
                          {intervenant.prenom} {intervenant.nom} - {intervenant.type}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut
                    </label>
                    <select
                      value={acteData.statut}
                      onChange={(e) => setActeData({ ...acteData, statut: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                    >
                      <option value="planifie">Planifié</option>
                      <option value="realise">Réalisé</option>
                    </select>
                  </div>
                </div>

                {/* Champs spécifiques */}
                {selectedActe?.champsSpecifiques && selectedActe.champsSpecifiques.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="font-medium text-gray-900">Informations spécifiques</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedActe.champsSpecifiques.map(champ => (
                        <div key={champ.nom}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {champ.label} {champ.obligatoire && '*'}
                          </label>
                          
                          {champ.type === 'text' && (
                            <input
                              type="text"
                              value={acteData.champsSpecifiques[champ.nom] || ''}
                              onChange={(e) => setActeData({
                                ...acteData,
                                champsSpecifiques: {
                                  ...acteData.champsSpecifiques,
                                  [champ.nom]: e.target.value
                                }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                            />
                          )}
                          
                          {champ.type === 'number' && (
                            <input
                              type="number"
                              value={acteData.champsSpecifiques[champ.nom] || ''}
                              onChange={(e) => setActeData({
                                ...acteData,
                                champsSpecifiques: {
                                  ...acteData.champsSpecifiques,
                                  [champ.nom]: e.target.value
                                }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                            />
                          )}
                          
                          {champ.type === 'date' && (
                            <input
                              type="date"
                              value={acteData.champsSpecifiques[champ.nom] || ''}
                              onChange={(e) => setActeData({
                                ...acteData,
                                champsSpecifiques: {
                                  ...acteData.champsSpecifiques,
                                  [champ.nom]: e.target.value
                                }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                            />
                          )}
                          
                          {champ.type === 'time' && (
                            <input
                              type="time"
                              value={acteData.champsSpecifiques[champ.nom] || ''}
                              onChange={(e) => setActeData({
                                ...acteData,
                                champsSpecifiques: {
                                  ...acteData.champsSpecifiques,
                                  [champ.nom]: e.target.value
                                }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                            />
                          )}
                          
                          {champ.type === 'select' && (
                            <select
                              value={acteData.champsSpecifiques[champ.nom] || ''}
                              onChange={(e) => setActeData({
                                ...acteData,
                                champsSpecifiques: {
                                  ...acteData.champsSpecifiques,
                                  [champ.nom]: e.target.value
                                }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                            >
                              <option value="">Sélectionner...</option>
                              {champ.options?.map(option => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          )}
                          
                          {champ.type === 'textarea' && (
                            <textarea
                              value={acteData.champsSpecifiques[champ.nom] || ''}
                              onChange={(e) => setActeData({
                                ...acteData,
                                champsSpecifiques: {
                                  ...acteData.champsSpecifiques,
                                  [champ.nom]: e.target.value
                                }
                              })}
                              rows="3"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description et notes */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={acteData.description}
                      onChange={(e) => setActeData({ ...acteData, description: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={acteData.notes}
                      onChange={(e) => setActeData({ ...acteData, notes: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                    />
                  </div>
                </div>

                {/* Coût */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coût (€)
                  </label>
                  <div className="relative max-w-xs">
                    <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      value={acteData.cout}
                      onChange={(e) => setActeData({ ...acteData, cout: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Documents */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Documents
                  </label>
                  <div
                    onDrop={handleDrop}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    className={`
                      border-2 border-dashed rounded-lg p-6 text-center
                      ${dragActive ? 'border-black bg-gray-50' : 'border-gray-300'}
                      ${uploadingFiles ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                      className="hidden"
                      id="file-upload"
                      disabled={uploadingFiles}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">
                        Glissez-déposez des fichiers ici ou cliquez pour sélectionner
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Images (JPG, PNG) et PDF uniquement (max 10MB)
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        📸 Images → Cloudinary (optimisées automatiquement)<br/>
                        📄 PDF → Firebase Storage
                      </p>
                    </label>
                  </div>
                  
                  {acteData.documents.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {acteData.documents.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">{doc.nom}</span>
                            <span className="text-xs text-gray-500">
                              ({(doc.taille / 1024).toFixed(0)} KB)
                            </span>
                            {doc.service === 'cloudinary' && <span className="text-xs">☁️</span>}
                            {doc.service === 'firebase' && <span className="text-xs">🔥</span>}
                          </div>
                          <button
                            onClick={() => setActeData({
                              ...acteData,
                              documents: acteData.documents.filter(d => d.id !== doc.id)
                            })}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>


{/* Section Rappel - NOUVELLE SECTION */}
{acteData.statut === 'planifie' && (
  <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
    <div className="flex items-start gap-3">
      <div className="flex-1">
        <label className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={acteData.rappel.active}
            onChange={(e) => setActeData({
              ...acteData,
              rappel: {
                ...acteData.rappel,
                active: e.target.checked
              }
            })}
            className="rounded text-blue-600 focus:ring-blue-500"
          />
          <span className="font-medium text-blue-900">
            <Clock className="inline h-4 w-4 mr-1" />
            Activer un rappel
          </span>
        </label>
        
        {acteData.rappel.active && (
          <div className="ml-6 space-y-3">
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-2">
                Rappel avant l'échéance (jours)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={acteData.rappel.joursAvant}
                  onChange={(e) => setActeData({
                    ...acteData,
                    rappel: {
                      ...acteData.rappel,
                      joursAvant: parseInt(e.target.value) || 7
                    }
                  })}
                  className="w-24 px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-blue-700">
                  jour{acteData.rappel.joursAvant > 1 ? 's' : ''} avant
                </span>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                Un rappel sera affiché {acteData.rappel.joursAvant} jour{acteData.rappel.joursAvant > 1 ? 's' : ''} avant la date prévue
                ({new Date(new Date(acteData.dateActe).getTime() - acteData.rappel.joursAvant * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')})
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="flex-shrink-0">
        <AlertCircle className="h-5 w-5 text-blue-500" />
      </div>
    </div>
  </div>
)}



{/* Section Récurrence - AVEC RAPPEL SÉPARÉ */}
<div className="border rounded-lg p-4">
  <label className="flex items-center gap-2 mb-4">
    <input
      type="checkbox"
      checked={acteData.recurrence.active}
      onChange={(e) => {
        const isActive = e.target.checked;
        setActeData({
          ...acteData,
          recurrence: {
            ...acteData.recurrence,
            active: isActive
          }
        });
      }}
      className="rounded text-black focus:ring-gray-700"
    />
    <span className="font-medium">
      <Repeat className="inline h-4 w-4 mr-1" />
      Acte récurrent
    </span>
  </label>
  
  {acteData.recurrence.active && (
    <div className="space-y-4">
      {/* Type de récurrence */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Fréquence de récurrence
        </label>
        <select
          value={acteData.recurrence.typeRecurrence}
          onChange={(e) => {
            setActeData({
              ...acteData,
              recurrence: {
                ...acteData.recurrence,
                typeRecurrence: e.target.value
              }
            });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
        >
          <option value="hebdomadaire">Hebdomadaire (chaque semaine)</option>
          <option value="mensuel">Mensuel (chaque mois)</option>
          <option value="trimestriel">Trimestriel (tous les 3 mois)</option>
          <option value="semestriel">Semestriel (tous les 6 mois)</option>
          <option value="annuel">Annuel (chaque année)</option>
        </select>
      </div>

      {/* NOUVEAU : Rappel pour les actes récurrents futurs */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={acteData.recurrence.rappelRecurrent?.active || false}
            onChange={(e) => setActeData({
              ...acteData,
              recurrence: {
                ...acteData.recurrence,
                rappelRecurrent: {
                  ...acteData.recurrence.rappelRecurrent,
                  active: e.target.checked
                }
              }
            })}
            className="rounded text-green-600 focus:ring-green-500"
          />
          <span className="text-sm font-medium text-green-900">
            <Clock className="inline h-3 w-3 mr-1" />
            Activer un rappel pour les prochains actes récurrents
          </span>
        </label>
        
        {acteData.recurrence.rappelRecurrent?.active && (
          <div className="ml-6">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="30"
                value={acteData.recurrence.rappelRecurrent?.joursAvant || 7}
                onChange={(e) => setActeData({
                  ...acteData,
                  recurrence: {
                    ...acteData.recurrence,
                    rappelRecurrent: {
                      ...acteData.recurrence.rappelRecurrent,
                      joursAvant: parseInt(e.target.value) || 7
                    }
                  }
                })}
                className="w-20 px-2 py-1 border border-green-300 rounded text-sm"
              />
              <span className="text-sm text-green-700">
                jour{(acteData.recurrence.rappelRecurrent?.joursAvant || 7) > 1 ? 's' : ''} avant chaque échéance
              </span>
            </div>
            <p className="text-xs text-green-600 mt-2">
              Chaque acte futur aura un rappel {acteData.recurrence.rappelRecurrent?.joursAvant || 7} jour{(acteData.recurrence.rappelRecurrent?.joursAvant || 7) > 1 ? 's' : ''} avant sa date
            </p>
          </div>
        )}
      </div>

      {/* Avertissement si statut = réalisé */}
      {acteData.statut === 'realise' && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span className="font-medium">Acte créé en "réalisé"</span>
          </p>
          <p className="text-xs text-amber-700 mt-1">
            Le prochain acte sera automatiquement créé pour le {new Date(calculateNextDate(acteData.dateActe, acteData.recurrence.typeRecurrence)).toLocaleDateString('fr-FR')}.
            {acteData.recurrence.rappelRecurrent?.active && ` Un rappel sera affiché ${acteData.recurrence.rappelRecurrent.joursAvant} jour${acteData.recurrence.rappelRecurrent.joursAvant > 1 ? 's' : ''} avant.`}
          </p>
        </div>
      )}
    </div>
  )}
</div>


              </div>
            )}
          </div>

          {/* Pied de page */}
          <div className="p-6 border-t flex items-center justify-between">
            <div>
              {step === 2 && (
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Retour
                </button>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              
              {step === 1 ? (
                <button
                  onClick={() => setStep(2)}
                  disabled={selectedAnimaux.length === 0}
                  className={`
                    px-4 py-2 rounded-lg flex items-center gap-2
                    ${selectedAnimaux.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-black text-white hover:bg-gray-800'}
                  `}
                >
                  Suivant
                  <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={saving || !selectedActe}
                  className={`
                    px-4 py-2 rounded-lg flex items-center gap-2
                    ${saving || !selectedActe
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-black text-white hover:bg-gray-800'}
                  `}
                >
                  {saving ? 'Création...' : 'Créer l\'acte'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NouvelActeModal;